import { z } from "zod";
import { preferredConnectorIntegration } from "./connector-integration-order.js";
const slugSchema = z.string().trim().min(1).regex(/^[a-z0-9][a-z0-9_.-]*$/u);
export const integrationRuntimeCapabilitySchema = z.object({
    pluginSlug: slugSchema,
    toolSlug: slugSchema,
    label: z.string().trim().min(1),
    description: z.string().trim().min(1),
    surface: z.enum(["electric-tool", "mcp", "cli", "http"]),
    access: z.enum(["read", "write", "destructive", "unknown"]),
    approval: z.enum(["never", "always", "policy", "unknown"]),
}).strict();
export const integrationRuntimePlaneSchema = z.object({
    supported: z.boolean(),
    available: z.boolean(),
    connected: z.boolean(),
    adapters: z.array(slugSchema),
    capabilities: z.array(integrationRuntimeCapabilitySchema),
}).strict();
export const integrationSyncSourceSchema = z.object({
    provider: slugSchema,
    status: z.string().trim().min(1),
    connected: z.boolean(),
    enabled: z.boolean(),
    entities: z.array(z.string().trim().min(1)),
    lastSuccessfulSyncAt: z.number().int().nonnegative().optional(),
    lastError: z.string().trim().min(1).optional(),
}).strict();
export const integrationSyncPlaneSchema = z.object({
    supported: z.boolean(),
    connected: z.boolean(),
    enabled: z.boolean(),
    sources: z.array(integrationSyncSourceSchema),
}).strict();
export const integrationCapabilityNodeSchema = z.object({
    id: slugSchema,
    label: z.string().trim().min(1),
    description: z.string().trim().min(1),
    pluginSlugs: z.array(slugSchema),
    connectorProviders: z.array(slugSchema),
    runtime: integrationRuntimePlaneSchema,
    sync: integrationSyncPlaneSchema,
}).strict();
export const integrationCapabilityGraphSchema = z.object({
    schemaVersion: z.literal(1),
    integrations: z.array(integrationCapabilityNodeSchema),
}).strict().superRefine((graph, context) => {
    const ids = new Set(graph.integrations.map(({ id }) => id));
    if (ids.size !== graph.integrations.length) {
        context.addIssue({
            code: z.ZodIssueCode.custom,
            path: ["integrations"],
            message: "Integration capability graph contains duplicate integration ids.",
        });
    }
});
/** Project canonical graph identities directly from runtime plugin metadata. */
export function integrationCapabilityProvidersFromPlugins(plugins) {
    return plugins.map((plugin) => ({
        id: plugin.slug,
        label: plugin.label,
        description: plugin.description,
        pluginSlugs: [plugin.slug],
        connectorProviders: plugin.integration?.connectorProviders ?? [],
    }));
}
/**
 * Build the client-safe authority for integration capability and connection
 * truth. Runtime execution and background ingestion deliberately remain
 * separate planes: one can be connected and usable while the other is absent,
 * disabled, or unhealthy.
 */
export function buildIntegrationCapabilityGraph(input) {
    const plugins = new Map(input.plugins.map((plugin) => [plugin.slug, plugin]));
    const setup = new Map(input.setupStatuses.map((status) => [status.pluginSlug, status]));
    const policies = input.adapterPolicies ?? [];
    const integrations = input.providers.map((provider) => {
        const pluginSlugs = sortedUnique(provider.pluginSlugs ?? []);
        const connectorProviders = sortedUnique(provider.connectorProviders ?? []);
        const runtimePlugins = pluginSlugs.flatMap((slug) => {
            const plugin = plugins.get(slug);
            return plugin ? [plugin] : [];
        });
        const capabilities = runtimePlugins
            .flatMap((plugin) => plugin.tools.map((tool) => runtimeCapability(plugin.slug, tool)))
            .sort(compareRuntimeCapability);
        const runtimeSupported = pluginSlugs.length > 0;
        const runtimeAvailable = runtimePlugins.some((plugin) => plugin.enabled);
        const runtimeConnected = runtimeSupported && pluginSlugs.every((slug) => setup.get(slug)?.configured === true);
        const sources = connectorProviders.map((connectorProvider) => {
            const connector = preferredConnectorIntegration(input.connectorIntegrations, connectorProvider);
            const connected = connector !== undefined && connector.enabled &&
                !["disabled", "not-configured"].includes(connector.status);
            return {
                provider: connectorProvider,
                status: connector?.status ?? "not-configured",
                connected,
                enabled: connector?.enabled === true,
                entities: sortedUnique(connector?.enabledEntities ?? []),
                ...(connector?.lastSuccessfulSyncAt !== undefined
                    ? { lastSuccessfulSyncAt: connector.lastSuccessfulSyncAt }
                    : {}),
                ...(connector?.lastError ? { lastError: connector.lastError } : {}),
            };
        });
        const syncSupported = connectorProviders.length > 0;
        const syncConnected = syncSupported && sources.every(({ connected }) => connected);
        const relevantPolicies = policies.filter(({ providerSlug }) => connectorProviders.includes(providerSlug));
        const syncEnabled = syncConnected && (relevantPolicies.length === 0 ||
            relevantPolicies.every((policy) => policy.enabled && policy.syncAdapter === "native"));
        const adapters = sortedUnique([
            ...runtimePlugins.flatMap(runtimeAdapters),
            ...relevantPolicies.filter(({ enabled }) => enabled)
                .flatMap(({ taskAdapter }) => taskAdapter ? [taskAdapter] : []),
        ]);
        return {
            id: provider.id,
            label: provider.label,
            description: provider.description,
            pluginSlugs,
            connectorProviders,
            runtime: {
                supported: runtimeSupported,
                available: runtimeAvailable,
                connected: runtimeConnected,
                adapters,
                capabilities,
            },
            sync: { supported: syncSupported, connected: syncConnected, enabled: syncEnabled, sources },
        };
    }).sort((left, right) => left.id.localeCompare(right.id));
    return integrationCapabilityGraphSchema.parse({ schemaVersion: 1, integrations });
}
function runtimeCapability(pluginSlug, tool) {
    return {
        pluginSlug,
        toolSlug: tool.name,
        label: tool.label,
        description: tool.description,
        surface: tool.runtimeSurface,
        access: tool.access?.sideEffect ?? "unknown",
        approval: tool.access?.approval ?? "unknown",
    };
}
function runtimeAdapters(plugin) {
    const integration = plugin.integration?.provider;
    return sortedUnique([
        ...(integration ? [integration] : []),
        ...plugin.tools.map(({ runtimeSurface }) => runtimeSurface),
    ]);
}
function compareRuntimeCapability(left, right) {
    return `${left.pluginSlug}:${left.toolSlug}`.localeCompare(`${right.pluginSlug}:${right.toolSlug}`);
}
function sortedUnique(values) {
    return [...new Set(values)].sort((left, right) => left.localeCompare(right));
}
